import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "kind_cart_v2";

// Cart items are non-sensitive (price_id, name, price, qty) and persist
// across reloads via localStorage. No auth/PII is stored here.
export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        if (typeof window === "undefined") return [];
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch (error) {
            console.warn("Failed to persist cart to localStorage:", error);
        }
    }, [items]);

    const add = useCallback((product, qty = 1) => {
        const id = product.price_id || product.id;
        setItems((curr) => {
            const found = curr.find((x) => x.price_id === id);
            if (found) {
                return curr.map((x) =>
                    x.price_id === id ? { ...x, quantity: x.quantity + qty } : x
                );
            }
            return [
                ...curr,
                { price_id: id, name: product.name, price: product.price, image_url: product.image_url, quantity: qty },
            ];
        });
    }, []);

    const remove = useCallback((priceId) => {
        setItems((curr) => curr.filter((x) => x.price_id !== priceId));
    }, []);

    const updateQty = useCallback((priceId, qty) => {
        setItems((curr) =>
            curr.map((x) => (x.price_id === priceId ? { ...x, quantity: Math.max(1, qty) } : x))
        );
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const subtotal = useMemo(
        () => items.reduce((s, x) => s + x.price * x.quantity, 0),
        [items]
    );
    const count = useMemo(
        () => items.reduce((s, x) => s + x.quantity, 0),
        [items]
    );

    const value = useMemo(
        () => ({ items, add, remove, updateQty, clear, subtotal, count }),
        [items, add, remove, updateQty, clear, subtotal, count]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
